// Shared utilities for nutrition forms (Add and Update Food Entry).
//
// Catalog data (groups, labels, units, slugs, ordering) lives on the backend
// and is fetched at runtime via `useNutrientCatalog()` — see
// `src/hooks/useNutrientCatalog.ts`. This module keeps only the small set of
// pure helpers the food forms call directly.

import type { FoodCapabilities } from "@/lib/food-version-api";

/**
 * Slugify function - converts text to lowercase with underscores
 * Preserves existing underscores in the text
 */
export const slugify = (text: string): string => {
  return text
    .toLowerCase()
    .replace(/\s+/g, "_")
    .replace(/[^\w_]/g, "");
};

/**
 * Fields that should be excluded from nutrient_values payload.
 * These are top-level food properties or classification metadata, not
 * nutrient entries.
 */
export const excludedFromNutrientValues = [
  "name",
  "foodName",
  "hindiName",
  "fullName",
  "otherName",
  "brand",
  "source",
  "sourceId",
  "sourceLink",
  "sourceWeb",
  "sourcePdf",
  "sourceLinkPdf",
  // Legacy top-level form fields. The catalog's "energy" and "water" rows are NOT
  // excluded: they are written as nutrients and the backend decides where each is
  // stored (Energy -> energy_kcal, Water -> the nutrient list).
  "energyKcal",
  "waterG",
  "ashG",
  // Catalog "ash" is NOT excluded: the backend has no top-level ash column, so
  // the nutrient_values row is Ash's only home and must be written through.
  "favourite",
  "favorite",
  "homemade",
  "foodType",
  // The nested serving on a per-serving supplement — form inputs, not nutrients
  "servingUnitCode",
  "servingUnitName",
  "servingMultiplier",
  "servingWeightG",
  // Classification tags
  "cuisineTags",
  "feedingCategories",
  "mealTypes",
  "conditionTags",
  "allergenTags",
  "ageSuitabilityTags",
  "contextTags",
  // Choices
  "texture",
  "sourceConfidence",
  // Dietary composition fields
  "containsMeat",
  "containsFish",
  "containsEgg",
  "containsDairy",
  "containsHoney",
  "containsGelatin",
  "containsOnionGarlic",
  "containsRootVegetables",
  "containsAlcohol",
  "containsCaffeine",
];

/**
 * Build nutrient_values object from form data. The slug map is supplied by
 * the runtime catalog (`useNutrientCatalog().catalog.slugByFormField`) so
 * the frontend no longer maintains a hardcoded mapping.
 *
 * Only includes filled values and converts them to numbers.
 */
export const buildNutrientValues = (
  formData: Record<string, any>,
  slugByFormField: Record<string, string>
): Record<string, number> => {
  const nutrientValues: Record<string, number> = {};

  Object.entries(formData).forEach(([key, value]) => {
    if (excludedFromNutrientValues.includes(key)) return;
    const nutrientSlug = slugByFormField[key];
    if (!nutrientSlug) return;
    if (value === "" || value === undefined || value === null) return;

    const numValue = typeof value === "string" ? parseFloat(value) : value;
    if (!isNaN(numValue)) {
      nutrientValues[nutrientSlug] = numValue;
    }
  });

  return nutrientValues;
};

/**
 * Parse a number value from form data
 * Returns undefined if value is empty or invalid
 */
export const parseOptionalNumber = (value: any): number | undefined => {
  if (value === "" || value === undefined || value === null) {
    return undefined;
  }
  const numValue = typeof value === "string" ? parseFloat(value) : value;
  return !isNaN(numValue) ? numValue : undefined;
};

/**
 * Nutrient precision (clinical rule, Dr. Girish 2026-09-20): Energy is a whole
 * number; every other nutrient carries at most 2 decimal places. Display and
 * input only — storage keeps its own precision (energy 2dp, amounts 3dp).
 *
 * Stopgap, food library only. ZCS-480 moves this rule to the backend nutrient
 * catalog; once that ships, read precision from there and keep this as the
 * fallback for a payload without it.
 */
const ENERGY_FIELD_NAMES = new Set(["energyKcal", "energy"]);

export const nutrientDecimals = (fieldName: string): number =>
  ENERGY_FIELD_NAMES.has(fieldName) ? 0 : 2;

/** Read-only display: fixed decimals ("12.30", "579"). "—" when not a number. */
export const formatNutrientAmount = (
  value: number | string | null | undefined,
  fieldName: string
): string => {
  const num = typeof value === "string" ? parseFloat(value) : value;
  if (num === null || num === undefined || !isFinite(num)) return "—";
  return num.toFixed(nutrientDecimals(fieldName));
};

/**
 * Editable input value: rounded to the field's precision, trailing zeros
 * trimmed ("12.3", not "12.300"). Non-numeric text is returned unchanged so
 * what the user typed is never silently swallowed.
 */
export const roundNutrientInput = (
  value: string | null | undefined,
  fieldName: string
): string => {
  if (value === "" || value === null || value === undefined) return "";
  const num = parseFloat(value);
  if (!isFinite(num)) return value;
  return String(Number(num.toFixed(nutrientDecimals(fieldName))));
};

/**
 * Food type options for dropdown
 */
export const FOOD_TYPE_OPTIONS = [
  { label: "Raw", value: "R" },
  { label: "Recipe", value: "C" },
  { label: "Supplement", value: "S" },
  { label: "Branded", value: "B" },
  { label: "Others", value: "O" },
] as const;

/** `food_type` code for a supplement. Supplements are the only foods that may
 *  be stored per-serving, and the only foods barred from recipe ingredients. */
export const SUPPLEMENT_FOOD_TYPE = "S";

/**
 * Resolve a stored food type code ("R") to its display label ("Raw").
 *
 * The API stores and returns the single-letter code from
 * `FoodItem.FoodTypeChoices`, so any read-only surface must translate before
 * rendering. Falls back to the raw value for a code this build doesn't know
 * (a new type added backend-first), and to an em dash for an absent one.
 */
export const getFoodTypeLabel = (code: string | null | undefined): string => {
  if (!code) return "—";
  return FOOD_TYPE_OPTIONS.find((o) => o.value === code)?.label ?? code;
};

/**
 * What a food's stored nutrient values describe.
 *
 * `100G` — values are per 100 g. Every food in the catalog today is this.
 * `SERV` — values are per one serving (a capsule, a scoop, a sachet), which
 *          is the only honest basis for a supplement with no meaningful weight.
 *
 * Settable on create for supplements only, and read-only afterwards: flipping
 * it on a live food would rescale every entry already pinned to it, so the API
 * refuses. Unrelated to `NutrientBasis` in AddRecipeForm, which is a display
 * toggle on the recipe nutrition panel.
 */
export type FoodNutrientBasis = "100G" | "SERV";

export const FOOD_NUTRIENT_BASIS_OPTIONS = [
  { label: "Per 100 g", value: "100G" },
  { label: "Per serving", value: "SERV" },
] as const;

export const FOOD_NUTRIENT_BASIS_LABELS: Record<FoodNutrientBasis, string> = {
  "100G": "Per 100 g",
  SERV: "Per serving",
};

/**
 * Rules for a FoodServing multiplier — "how many label servings is one of this
 * unit". Only meaningful on a `SERV` food: on a `100G` food the server derives
 * the multiplier from the weight and ignores anything sent.
 *
 * The column is decimal(7,4). The precision is not decoration: a label stated
 * per 3 capsules makes one capsule 0.3333, and rounding that to 0.33 bakes a 1%
 * error into every entry ever logged against the food.
 *
 * The value always comes off the printed label and is never inferred from the
 * unit's name — a panel printed "per 2 tablets" makes one tablet 0.5, and no
 * amount of reading the word "tablet" tells you that.
 */
export const SERVING_MULTIPLIER_MIN = 0.0001;
const SERVING_MULTIPLIER_DECIMALS_PATTERN = /^\d*(\.\d{1,4})?$/;

/** Validate a typed multiplier. Returns the message to show, or null if fine. */
export const validateServingMultiplier = (raw: string): string | null => {
  const value = raw.trim();
  if (!value) {
    return "Multiplier is required — this food's nutrients are stored per serving.";
  }
  const num = parseFloat(value);
  if (!Number.isFinite(num) || num < SERVING_MULTIPLIER_MIN) {
    return `Enter a multiplier of at least ${SERVING_MULTIPLIER_MIN}.`;
  }
  if (!SERVING_MULTIPLIER_DECIMALS_PATTERN.test(value)) {
    return "At most 4 decimal places (e.g. 0.3333).";
  }
  return null;
};

/**
 * Validate a serving's gram weight. Required on a `100G` food (the server
 * derives the multiplier from it, and columbus#406 now rejects both a missing
 * value and a zero); optional on a `SERV` food, because a capsule has no
 * meaningful gram figure. A supplied value still has to be a real weight.
 */
export const validateServingWeightG = (
  raw: string,
  required: boolean
): string | null => {
  const value = raw.trim();
  if (!value) {
    return required
      ? "Weight is required — this food's nutrients are stored per 100 g."
      : null;
  }
  const num = parseFloat(value);
  if (!Number.isFinite(num) || num <= 0) {
    return required
      ? "Enter a weight greater than 0."
      : "Leave this blank, or enter a weight greater than 0.";
  }
  return null;
};

/**
 * Convert a numeric amount between g, mg, and mcg.
 * If units are the same or unsupported, returns the original value.
 */
export const convertUnit = (
  value: number,
  fromUnit: string,
  toUnit: string
): number => {
  if (!isFinite(value)) return value;
  const f = fromUnit?.toLowerCase();
  const t = toUnit?.toLowerCase();
  if (!f || !t || f === t) return value;

  // Normalize micro symbol variants to 'mcg'
  const norm = (u: string) => (u === "μg" ? "mcg" : u);
  const fu = norm(f);
  const tu = norm(t);

  // Convert to milligrams as an intermediate unit
  const toMilligrams = (v: number, unit: string) => {
    switch (unit) {
      case "g":
        return v * 1000;
      case "mg":
        return v;
      case "mcg":
        return v / 1000;
      default:
        return v;
    }
  };

  const fromMilligrams = (v: number, unit: string) => {
    switch (unit) {
      case "g":
        return v / 1000;
      case "mg":
        return v;
      case "mcg":
        return v * 1000;
      default:
        return v;
    }
  };

  const mg = toMilligrams(value, fu);
  return fromMilligrams(mg, tu);
};

/**
 * Whether this food's servings can be added to, edited or retired from the
 * console, per the server.
 *
 * Reads `can_edit_servings`, which stays true on a reference-source food (IFCT,
 * USDA, USFDA): a serving is Zarity's own household-measure mapping, not the
 * reference's data. An API that predates the flag only sends `can_edit`, so
 * fall back to it, and to true when neither is sent so a payload without
 * capabilities doesn't wrongly lock the table.
 */
export const foodServingsEditable = (
  capabilities: FoodCapabilities | null | undefined
): boolean => capabilities?.can_edit_servings ?? capabilities?.can_edit ?? true;

/**
 * Why the API refuses a console write on this food, phrased for the person
 * reading it.
 *
 * The capability flags are server-owned and say only that a write would be
 * refused, never why. A retired food (`is_retired`) 404s every write. A
 * reference-source food — IFCT, USDA, USFDA — comes back 403
 * REFERENCE_SOURCE_LOCKED for its own profile, but not for its servings, so
 * that case only applies to `subject: "food"`. It names the food's own
 * `source` rather than keeping a copy of the backend's source list here, so a
 * new reference database added server-side still reads correctly.
 *
 * Call only when the matching capability is false; the wording assumes it.
 */
export const buildFoodLockedReason = (
  food: { source?: string | null; is_retired?: boolean } | null | undefined,
  name: string,
  subject: "servings" | "food" = "food"
): string => {
  if (food?.is_retired) {
    return subject === "servings"
      ? `${name} is retired, so its servings can't be changed.`
      : `${name} is retired and can't be edited.`;
  }
  const source = (food?.source || "").trim();
  if (source && subject === "food") {
    return `${name} comes from ${source}, a published reference database, and can't be edited here. Create a new food instead.`;
  }
  return subject === "servings"
    ? `${name}'s servings can't be changed from the console.`
    : "This food can't be edited from the console.";
};
